import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
// DTC-L: route Passport-sync `technician_type_shop_verified_at` write
// through the canonical clock for deterministic audit timestamps.
import { clock } from '@/utils/clock';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import { TopBar } from '@/components/layout/TopBar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Slider } from '@/components/ui/slider';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { Loader2, Save, User, Clock, Wrench, TrendingUp, ShieldCheck } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { calculateSkillTier, PROFICIENCY_CATEGORIES } from '@/constants/proficiencyCategories';
import {
  buildTechnicianConfigurationUpdate,
  LEGACY_TECHNICIAN_ROLE_COMPAT,
} from '@/utils/technicianConfigurationPayload';
import { syncTechnicianToPassport } from '@/utils/syncTechnicianToPassport';
import { SliderHelpTooltip } from '@/components/ui/SliderHelpTooltip';
import { PROFICIENCY_HELP } from '@/constants/sliderHelpContent';
import { TECHNICIAN_TYPE_ALL_OPTIONS, type TechnicianTypeKey } from '@/utils/resolveTechnicianTypeTitle';
import { TechnicianTrainingVerificationCard } from '@/components/training/TechnicianTrainingVerificationCard';
import { WorkerRoleSummary } from '@/components/roles/WorkerRoleSummary';

const technicianSchema = z.object({
  name: z.string().trim().min(1, 'Name is required').max(100, 'Name must be less than 100 characters'),
  // No `role`: `technicians.role` is a legacy compatibility column, not a
  // manager choice. See LEGACY_TECHNICIAN_ROLE_DOCTRINE.
  skillTier: z.coerce.number().min(1).max(10),
  shiftStart: z.string().min(1, 'Start time is required'),
  shiftEnd: z.string().min(1, 'End time is required'),
  employmentStatus: z.enum(['Active', 'Inactive', 'On Leave']),
  isPresent: z.boolean().default(true),
});

type TechnicianValues = z.infer<typeof technicianSchema>;

// Use the official proficiency categories from constants
const proficiencyCategories = PROFICIENCY_CATEGORIES;

const bayTypes = ['Lift', 'Flat', 'Alignment', 'Specialty'];
const specialtyOptions = ['Diesel', 'Electrical', 'Diagnostics', 'Body & Paint', 'Performance', 'Transmission'];

const bayTypeLabels: Record<string, string> = {
  '2-post': '2-Post',
  '4-post': '4-Post',
  'scissor': 'Scissor',
  'adas': 'ADAS',
  'alignment': 'Alignment',
  'wash': 'Wash',
  'inspection': 'Inspection',
  'general': 'Flat',
  'flat': 'Flat',
};

const TechnicianEditor = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { activeShop } = useAuth();
  const queryClient = useQueryClient();
  const isNew = id === 'new';
  
  const [isLoading, setIsLoading] = useState(!isNew);
  const [isSaving, setIsSaving] = useState(false);
  const [bayAssignments, setBayAssignments] = useState<string[]>([]);
  const [specialties, setSpecialties] = useState<string[]>([]);
  const [proficiencies, setProficiencies] = useState<Record<string, number>>({});
  const [assignedBays, setAssignedBays] = useState<any[]>([]);
  const [shopVerifiedType, setShopVerifiedType] = useState<string>('');
  const [technicianProfileId, setTechnicianProfileId] = useState<string | null>(null);

  const form = useForm<TechnicianValues>({
    resolver: zodResolver(technicianSchema),
    defaultValues: {
      name: '',
      skillTier: 3,
      shiftStart: '08:00',
      shiftEnd: '17:00',
      employmentStatus: 'Active',
      isPresent: true,
    },
  });

  useEffect(() => {
    const loadData = async () => {
      try {
        // Load bays assigned to this technician from shop_bays table
        const { data: bays, error: baysError } = await supabase
          .from('shop_bays')
          .select('id, name, bay_type')
          .eq('assigned_tech_id', id)
          .eq('is_active', true);

        if (baysError) throw baysError;
        if (bays) setAssignedBays(bays);

        // Load technician data if editing
        if (!isNew && id) {
          const { data, error } = await supabase
            .from('technicians')
            .select('*')
            .eq('id', id)
            .single();

          if (error) throw error;

          if (data) {
            form.reset({
              name: data.name,
              skillTier: data.skill_tier,
              shiftStart: data.shift_start,
              shiftEnd: data.shift_end,
              employmentStatus: data.employment_status as 'Active' | 'Inactive' | 'On Leave',
              isPresent: data.is_present,
            });
            setBayAssignments(data.bay_assignments || []);
            setSpecialties(data.specialties || []);
            setProficiencies((data.proficiencies as Record<string, number>) || {});

            // Load linked technician_profile for shop-verified title
            if (data.shop_id) {
              const { data: techUsers } = await supabase
                .from('technician_users')
                .select('user_id')
                .eq('technician_id', id!)
                .eq('shop_id', data.shop_id)
                .limit(1)
                .maybeSingle();
              
              if (techUsers?.user_id) {
                const { data: techProfile } = await supabase
                  .from('technician_profiles')
                  .select('id')
                  .eq('user_id', techUsers.user_id)
                  .maybeSingle();
                if (techProfile) {
                  setTechnicianProfileId(techProfile.id);
                  // Fetch shop verified type separately with cast
                  const { data: titleData } = await (supabase.from('technician_profiles') as any)
                    .select('technician_type_shop_verified')
                    .eq('id', techProfile.id)
                    .maybeSingle();
                  setShopVerifiedType(titleData?.technician_type_shop_verified || '');
                  setShopVerifiedType((techProfile as any).technician_type_shop_verified || '');
                }
              }
            }
          }
        }
      } catch (error: any) {
        toast({
          title: 'Error loading data',
          description: error.message,
          variant: 'destructive',
        });
        if (!isNew) navigate('/technicians');
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [id, isNew]);

  const onSubmit = async (values: TechnicianValues) => {
    setIsSaving(true);
    try {
      const techData = {
        name: values.name,
        skill_tier: values.skillTier,
        shift_start: values.shiftStart,
        shift_end: values.shiftEnd,
        employment_status: values.employmentStatus,
        is_present: values.isPresent,
        bay_assignments: bayAssignments,
        specialties,
        proficiencies,
        shop_id: activeShop!.id,
      };

      if (isNew) {
        // `technicians.role` is NOT NULL with no DEFAULT, and this branch
        // inserts directly rather than through the server. It receives the
        // existing compatibility constant — not a choice anybody makes, and it
        // confers nothing. See LEGACY_TECHNICIAN_ROLE_DOCTRINE.
        const { error } = await supabase
          .from('technicians')
          .insert([{ ...techData, role: LEGACY_TECHNICIAN_ROLE_COMPAT }]);
        if (error) throw error;
        toast({
          title: 'Technician created',
          description: `${values.name} has been added to your roster`,
        });
      } else {
        // Same boundary as TechnicianForm: an ordinary edit here must not
        // rewind CORE-E's demonstrated proficiency to whatever this page
        // happened to load. (Creation above still carries the opening estimate.)
        const { error } = await supabase
          .from('technicians')
          .update(buildTechnicianConfigurationUpdate(techData))
          .eq('id', id);
        if (error) throw error;

        // Sync to passport snapshot tables
        await syncTechnicianToPassport(id!);

        toast({
          title: 'Technician updated',
          description: 'Changes have been saved successfully',
        });
      }

      // Same invalidation as `TechnicianForm`: the Dayboard follows the
      // `technicians` realtime publication, but `shop-utilization-card` caches
      // whole rows — `hours` and the shift columns included — and divides the
      // shop's capacity by them. Invalidated by key, not by a page reload.
      queryClient.invalidateQueries({ queryKey: ['shop-utilization-card'] });
      queryClient.invalidateQueries({ queryKey: ['payroll-employees'] });
      queryClient.invalidateQueries({ queryKey: ['technicians-facility'] });
      queryClient.invalidateQueries({ queryKey: ['passport-live-tech'] });
      queryClient.invalidateQueries({ queryKey: ['passport-live-tech-by-profile'] });

      navigate('/technicians');
    } catch (error: any) {
      toast({
        title: 'Error saving technician',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const toggleBay = (bay: string) => {
    setBayAssignments(prev =>
      prev.includes(bay) ? prev.filter(b => b !== bay) : [...prev, bay]
    );
  };

  const toggleSpecialty = (specialty: string) => {
    setSpecialties(prev =>
      prev.includes(specialty) ? prev.filter(s => s !== specialty) : [...prev, specialty]
    );
  };

  const updateProficiency = (category: string, value: number) => {
    const newProficiencies = { ...proficiencies, [category]: value };
    setProficiencies(newProficiencies);
    
    // Auto-calculate and update skill tier based on proficiencies
    const calculatedSkillTier = calculateSkillTier(newProficiencies);
    form.setValue('skillTier', calculatedSkillTier);
  };

  if (isLoading) {
    return (
      <div className="flex flex-col h-screen bg-background">
        <TopBar />
        <main className="flex-1 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </main>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-background">
      <TopBar />
      
      <main className="flex-1 overflow-auto">
        <div className="max-w-5xl mx-auto p-6">
          <div className="mb-6 flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold">{isNew ? 'New' : 'Edit'} Technician</h1>
              <p className="text-muted-foreground mt-1">Configure technician details and skills</p>
            </div>
            <Button variant="outline" onClick={() => navigate('/technicians')}>
              Back to List
            </Button>
          </div>

          <Tabs defaultValue="general" className="space-y-6">
            <TabsList>
              <TabsTrigger value="general" data-testid="tech-general-tab">General</TabsTrigger>
              <TabsTrigger value="skills" data-testid="tech-skills-tab">Skills & Proficiencies</TabsTrigger>
              <TabsTrigger value="schedule" data-testid="tech-schedule-tab">Schedule & Bays</TabsTrigger>
            </TabsList>

            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)}>
                <TabsContent value="general" className="space-y-6">
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <User className="h-5 w-5" />
                        Basic Information
                      </CardTitle>
                      <CardDescription>Personal and employment details</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <FormField
                        control={form.control}
                        name="name"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Full Name *</FormLabel>
                            <FormControl>
                              <Input {...field} placeholder="John Smith" autoComplete="off" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      {/* ONE ROLE SYSTEM. No roster-title picker: the legacy
                          `technicians.role` is a compatibility column, not a
                          manager choice. `membership_work_roles` is the role
                          authority. See LEGACY_TECHNICIAN_ROLE_DOCTRINE. */}

                      {/* The canonical roles, read-only, from the SAME
                          component Shop Profile uses. One report, one editor:
                          `manage-membership-roles` remains the only contract
                          that mutates work roles, and this page does not
                          become a second one. */}
                      <WorkerRoleSummary
                        technicianId={isNew ? null : (id ?? null)}
                        shopId={activeShop?.id}
                      />
                      <FormField
                        control={form.control}
                        name="skillTier"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Overall Skill Tier: {field.value}</FormLabel>
                            <FormControl>
                              <Slider
                                min={1}
                                max={10}
                                step={1}
                                value={[field.value]}
                                onValueChange={(vals) => field.onChange(vals[0])}
                                disabled
                              />
                            </FormControl>
                            <FormDescription>Auto-calculated from weighted proficiencies (1 = Beginner, 10 = Master Technician)</FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="employmentStatus"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Employment Status *</FormLabel>
                            <Select onValueChange={field.onChange} value={field.value}>
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="Active">Active</SelectItem>
                                <SelectItem value="Inactive">Inactive</SelectItem>
                                <SelectItem value="On Leave">On Leave</SelectItem>
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="isPresent"
                        render={({ field }) => (
                          <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                            <FormControl>
                              <Checkbox
                                checked={field.value}
                                onCheckedChange={field.onChange}
                              />
                            </FormControl>
                            <div className="space-y-1 leading-none">
                              <FormLabel>Currently Present</FormLabel>
                              <FormDescription>
                                Mark if technician is on-site today
                              </FormDescription>
                            </div>
                          </FormItem>
                        )}
                      />
                    </CardContent>
                  </Card>

                  {/* Shop-Verified Technician Type */}
                  {technicianProfileId && (
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <ShieldCheck className="h-5 w-5" />
                          Technician Type (Confirmed by Shop)
                        </CardTitle>
                        <CardDescription>
                          Override the technician's title. This takes highest precedence everywhere.
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <Select
                          value={shopVerifiedType || '_none'}
                          onValueChange={async (val) => {
                            const newType = val === '_none' ? null : val;
                            setShopVerifiedType(newType || '');

                            // Persist to technician_profiles
                            const updateData: any = newType
                              ? {
                                  technician_type_shop_verified: newType,
                                  technician_type_shop_verified_at: clock.now().toISOString(),
                                  technician_type_shop_verified_by: (await supabase.auth.getUser()).data.user?.id,
                                  technician_type_source: 'shop_verified',
                                }
                              : {
                                  // Guardrail #3: Clear reverts source correctly
                                  technician_type_shop_verified: null,
                                  technician_type_shop_verified_at: null,
                                  technician_type_shop_verified_by: null,
                                  technician_type_source: 'none', // Will be corrected to 'inferred' if signal exists
                                };

                            await (supabase.from('technician_profiles') as any)
                              .update(updateData)
                              .eq('id', technicianProfileId);

                            toast({
                              title: newType ? 'Technician type verified' : 'Verification cleared',
                            });
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Not set" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="_none">— Clear —</SelectItem>
                            {TECHNICIAN_TYPE_ALL_OPTIONS.map((opt) => (
                              <SelectItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </CardContent>
                    </Card>
                  )}

                  {/*
                    Batch 18-E12: tiny manager-facing training verification UI.
                    The card itself is feature-flag-gated (managerTrainingVerification);
                    when disabled it returns null and the data fetch is skipped.
                  */}
                  <TechnicianTrainingVerificationCard
                    technicianProfileId={technicianProfileId}
                    technicianName={form.getValues('name')}
                  />
                </TabsContent>

                <TabsContent value="skills" className="space-y-6">
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <TrendingUp className="h-5 w-5" />
                        Proficiency Levels
                      </CardTitle>
                      <CardDescription>
                        {isNew
                          ? 'Your opening estimate per category (0-100%). OptiBay refines these from work the technician actually completes.'
                          : 'Demonstrated from completed work — shown here rather than edited.'}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4" data-testid="tech-skill-editor">
                      <div data-testid="proficiency-sliders">
                        {proficiencyCategories.map(category => (
                          <div key={category}>
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-1.5">
                                <label className="text-sm font-medium">{category}</label>
                                <SliderHelpTooltip
                                  title={`${category} Proficiency`}
                                  description={PROFICIENCY_HELP[category]?.description || `Influences preference for ${category.toLowerCase()} work.`}
                                  subcategories={PROFICIENCY_HELP[category]?.subcategories}
                                />
                              </div>
                              <span className="text-sm text-muted-foreground">
                                {proficiencies[category] || 0}%
                              </span>
                            </div>
                            <Slider
                              min={0}
                              max={100}
                              step={5}
                              value={[proficiencies[category] || 0]}
                              onValueChange={(vals) => updateProficiency(category, vals[0])}
                              /* CORE-W requirement 7 — CORE-E's after creation. */
                              disabled={!isNew}
                            />
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle>Specialties</CardTitle>
                      <CardDescription>Select areas of expertise</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 gap-3">
                        {specialtyOptions.map(specialty => (
                          <div key={specialty} className="flex items-center space-x-2">
                            <Checkbox
                              id={specialty}
                              checked={specialties.includes(specialty)}
                              onCheckedChange={() => toggleSpecialty(specialty)}
                            />
                            <label htmlFor={specialty} className="text-sm cursor-pointer">
                              {specialty}
                            </label>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="schedule" className="space-y-6">
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Clock className="h-5 w-5" />
                        Shift Hours
                      </CardTitle>
                      <CardDescription>Standard working hours</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4" data-testid="tech-schedule-editor">
                      <div className="grid grid-cols-2 gap-4">
                        <FormField
                          control={form.control}
                          name="shiftStart"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Start Time *</FormLabel>
                              <FormControl>
                                <Input type="time" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="shiftEnd"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>End Time *</FormLabel>
                              <FormControl>
                                <Input type="time" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Wrench className="h-5 w-5" />
                        Bay Assignments
                      </CardTitle>
                      <CardDescription>Bays assigned in Shop Floor Editor</CardDescription>
                    </CardHeader>
                    <CardContent>
                      {assignedBays.length === 0 ? (
                        <p className="text-sm text-muted-foreground">
                          No bays assigned yet. Assign bays to this technician in the Shop Floor Editor.
                        </p>
                      ) : (
                        <div className="space-y-2">
                          {assignedBays.map(bay => (
                            <div 
                              key={bay.id} 
                              className="flex items-center justify-between p-2 border rounded-md"
                            >
                              <div>
                                <p className="font-medium text-sm">{bay.name}</p>
                                <p className="text-xs text-muted-foreground">{bayTypeLabels[bay.bay_type] || bay.bay_type}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      <p className="text-xs text-muted-foreground mt-4">
                        To modify bay assignments, use the Shop Floor Editor page.
                      </p>
                    </CardContent>
                  </Card>
                </TabsContent>

                <div className="flex justify-end gap-3 mt-6">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate('/technicians')}
                    disabled={isSaving}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={isSaving} data-testid="save-tech-button">
                    {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    <Save className="mr-2 h-4 w-4" />
                    {isNew ? 'Create' : 'Save'} Technician
                  </Button>
                </div>
              </form>
            </Form>
          </Tabs>
        </div>
      </main>
    </div>
  );
};

export default TechnicianEditor;
